/**
 * Round 95 — useDeepLinkParams hook.
 *
 * Bot pages call this on mount to read the Round 94 deep-link
 * query params (`asset_id`, `task_id`, `return_to`, `mode`,
 * `asset_type`) written by <TaskActionButton />.
 *
 * Round 101 (2026-07-23) — universal completion routing.
 * DailyMissionCard now threads the FULL workflow context blob
 * (`mission_id`, `business_id`, `mission_title`, `niche`,
 * `target_audience`, `preferred_platform`, `publishing_history`,
 * `asset_payload_summary`) through a base64-JSON `wctx` param
 * so the builder receives the EXACT mission the customer
 * approved — not a regenerated echo from generic Business
 * Memory. `mission` on the returned object is the decoded
 * context (or null when absent).
 *
 * Zero-behavior when no params present — the page keeps its
 * existing create-new default.
 *
 * Returns a stable object across renders so useEffect deps behave.
 */
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const decodeWctx = (raw) => {
  if (!raw) return null;
  try {
    const json = decodeURIComponent(escape(window.atob(raw)));
    const parsed = JSON.parse(json);
    return (parsed && typeof parsed === 'object') ? parsed : null;
  } catch {
    return null;
  }
};

export default function useDeepLinkParams() {
  const location = useLocation();
  return useMemo(() => {
    const p = new URLSearchParams(location.search);
    const params = {
      asset_id: p.get('asset_id') || null,
      task_id: p.get('task_id') || null,
      return_to: p.get('return_to') || null,
      mode: p.get('mode') || null,
      asset_type: p.get('asset_type') || null,
      mission_id: p.get('mission_id') || null,
      business_id: p.get('business_id') || null,
      mission: decodeWctx(p.get('wctx')),
    };
    // ``hasDeepLink`` is true when the page was launched via
    // <TaskActionButton /> with a workflow_target. Bot pages use
    // this to decide whether to auto-open the edit modal.
    params.hasDeepLink = Boolean(
      params.asset_id || params.task_id || params.mission_id,
    );
    return params;
  }, [location.search]);
}
